import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  AnimatePresence,
  motion,
  useMotionTemplate,
  useMotionValue,
  useReducedMotion,
  useScroll,
  useSpring,
  useTransform,
} from 'framer-motion';
import { ChevronLeft, ChevronRight, ExternalLink } from 'lucide-react';
import SectionHeader from './SectionHeader';
import { useLocale } from '../context/LocaleContext';

const FILTER_ALL = 'all';
const PROJECTS_PER_PAGE = 9;

const GRID_COLS_DESKTOP = 3;
const GRID_COLS_MOBILE = 2;
const GRID_BREAKPOINT = 980;

const getGridDistance = (indexA, indexB, cols) => {
  const rowA = Math.floor(indexA / cols);
  const colA = indexA % cols;
  const rowB = Math.floor(indexB / cols);
  const colB = indexB % cols;
  return Math.abs(rowA - rowB) + Math.abs(colA - colB);
};

const getTileMotion = (gridIndex, hoveredGridIndex, cols, reduceMotion) => {
  if (reduceMotion || hoveredGridIndex === null) {
    return { scale: 1, opacity: 1, y: 0, filter: 'saturate(1) brightness(1)' };
  }

  const distance = getGridDistance(gridIndex, hoveredGridIndex, cols);

  if (distance === 0) {
    return {
      scale: 1.045,
      opacity: 1,
      y: -3,
      filter: 'saturate(1.12) brightness(1.06)',
    };
  }

  if (distance === 1) {
    return {
      scale: 0.975,
      opacity: 0.78,
      y: 1,
      filter: 'saturate(0.82) brightness(0.92)',
    };
  }

  if (distance === 2) {
    return {
      scale: 0.955,
      opacity: 0.58,
      y: 2,
      filter: 'saturate(0.7) brightness(0.86)',
    };
  }

  return {
    scale: 0.94,
    opacity: 0.42,
    y: 3,
    filter: 'saturate(0.62) brightness(0.8)',
  };
};

const ProjectTile = ({
  project,
  index,
  gridIndex,
  cols,
  isActive,
  hoveredGridIndex,
  reduceMotion,
  onSelect,
  onHover,
  onHoverEnd,
}) => {
  const isHovered = hoveredGridIndex === gridIndex;
  const motionState = getTileMotion(gridIndex, hoveredGridIndex, cols, reduceMotion);

  return (
    <motion.button
      type="button"
      role="tab"
      aria-selected={isActive}
      aria-label={project.title}
      className={`projects-structure__tile${isActive ? ' is-active' : ''}${isHovered ? ' is-hovered' : ''}${hoveredGridIndex !== null ? ' is-grid-hovering' : ''}`}
      onClick={() => onSelect(index)}
      onMouseEnter={() => onHover(gridIndex)}
      onFocus={() => onHover(gridIndex)}
      onBlur={onHoverEnd}
      animate={motionState}
      transition={{ duration: 0.34, ease: [0.16, 0.84, 0.3, 1] }}
      style={{ zIndex: isHovered ? 3 : 1 }}
    >
      <span className="projects-structure__tile-media">
        <motion.img
          src={project.image}
          alt=""
          loading="lazy"
          animate={{
            scale: isHovered && !reduceMotion ? 1.08 : 1,
          }}
          transition={{ duration: 0.45, ease: [0.16, 0.84, 0.3, 1] }}
        />
      </span>
      <span className="projects-structure__tile-body">
        <span className="projects-structure__tile-title">{project.title}</span>
        <span className="projects-structure__tile-meta">{project.category}</span>
      </span>
    </motion.button>
  );
};

const PREVIEW_SPRING = { stiffness: 140, damping: 22, mass: 0.65 };
const PREVIEW_SCROLL_SPRING = { stiffness: 72, damping: 22, mass: 0.45 };

const ProjectPreviewPanel = ({
  project,
  shots,
  shotIndex,
  hasMultipleShots,
  direction,
  reduceMotion,
  slideVariants,
  parallaxContainerRef,
  onPrevShot,
  onNextShot,
  onPrev,
  onNext,
  t,
}) => {
  const previewRef = useRef(null);
  const pointerX = useMotionValue(0);
  const pointerY = useMotionValue(0);

  const { scrollYProgress } = useScroll({
    target: parallaxContainerRef,
    offset: ['start end', 'end start'],
  });
  const scrollParallaxY = useSpring(
    useTransform(scrollYProgress, [0, 0.5, 1], [42, 0, -42]),
    PREVIEW_SCROLL_SPRING,
  );

  const rotateX = useSpring(useTransform(pointerY, [-0.5, 0.5], [2, -2]), PREVIEW_SPRING);
  const rotateY = useSpring(useTransform(pointerX, [-0.5, 0.5], [-2, 2]), PREVIEW_SPRING);
  const glareX = useSpring(useTransform(pointerX, [-0.5, 0.5], [40, 60]), PREVIEW_SPRING);
  const glareY = useSpring(useTransform(pointerY, [-0.5, 0.5], [38, 62]), PREVIEW_SPRING);
  const glareBackground = useMotionTemplate`radial-gradient(circle at ${glareX}% ${glareY}%, rgba(255, 255, 255, 0.1) 0%, rgba(184, 146, 72, 0.06) 18%, transparent 42%)`;

  const resetPointer = useCallback(() => {
    pointerX.set(0);
    pointerY.set(0);
  }, [pointerX, pointerY]);

  useEffect(() => {
    resetPointer();
  }, [project.id, shotIndex, resetPointer]);

  const handlePointerMove = (event) => {
    const rect = previewRef.current?.getBoundingClientRect();
    if (!rect) return;
    pointerX.set((event.clientX - rect.left) / rect.width - 0.5);
    pointerY.set((event.clientY - rect.top) / rect.height - 0.5);
  };

  return (
    <motion.article
      ref={previewRef}
      className="projects-structure__preview"
      custom={direction}
      variants={reduceMotion ? undefined : slideVariants}
      initial={reduceMotion ? false : 'enter'}
      animate="center"
      exit={reduceMotion ? undefined : 'exit'}
      transition={{ duration: 0.28, ease: [0.16, 0.84, 0.3, 1] }}
      onPointerMove={reduceMotion ? undefined : handlePointerMove}
      onPointerLeave={reduceMotion ? undefined : resetPointer}
      style={
        reduceMotion
          ? undefined
          : {
              y: scrollParallaxY,
              rotateX,
              rotateY,
              transformStyle: 'preserve-3d',
              transformPerspective: 1000,
            }
      }
    >
      <svg
        className="projects-structure__corner-mark projects-structure__corner-mark--bl"
        viewBox="0 0 44 44"
        aria-hidden="true"
      >
        <line x1="0" y1="28" x2="28" y2="0" />
        <line x1="8" y1="36" x2="36" y2="8" />
        <line x1="16" y1="44" x2="44" y2="16" />
      </svg>
      <svg
        className="projects-structure__corner-mark projects-structure__corner-mark--br"
        viewBox="0 0 44 44"
        aria-hidden="true"
      >
        <line x1="44" y1="28" x2="16" y2="0" />
        <line x1="36" y1="36" x2="8" y2="8" />
        <line x1="28" y1="44" x2="0" y2="16" />
      </svg>

      <div className="projects-structure__hero">
        <div className="projects-structure__hero-media">
          <img
            key={`${project.id}-${shotIndex}`}
            src={shots[shotIndex]}
            alt={project.title}
            className="projects-structure__hero-image"
          />
          {!reduceMotion ? (
            <motion.span
              className="projects-structure__hero-glare"
              aria-hidden="true"
              style={{ background: glareBackground }}
            />
          ) : null}
          <span className="projects-structure__hero-overlay" aria-hidden="true" />
        </div>
        {hasMultipleShots ? (
          <div className="projects-structure__hero-controls">
            <button
              type="button"
              className="projects-structure__hero-btn"
              onClick={onPrevShot}
              aria-label={t('home_project_shot_prev_aria')}
            >
              <ChevronLeft size={15} />
            </button>
            <button
              type="button"
              className="projects-structure__hero-btn"
              onClick={onNextShot}
              aria-label={t('home_project_shot_next_aria')}
            >
              <ChevronRight size={15} />
            </button>
          </div>
        ) : null}
      </div>
      <div className="projects-structure__content">
        <h4 className="projects-structure__project-title">{project.title}</h4>
        <p className="projects-structure__project-desc">{project.description}</p>
        <div className="projects-structure__chips">
          {project.technologies.map((tech) => (
            <span key={`${project.id}-${tech}`} className="projects-structure__chip">
              {tech}
            </span>
          ))}
        </div>
        <div className="projects-structure__actions">
          <button
            type="button"
            className="rihen-btn projects-structure__nav-btn"
            onClick={onPrev}
            aria-label={t('home_projects_prev_aria')}
          >
            <ChevronLeft size={12} aria-hidden="true" />
            {t('home_projects_prev_aria')}
          </button>
          {project.links?.live && project.links.live !== '#' ? (
            <a
              href={project.links.live}
              className="rihen-btn rihen-btn-primary projects-structure__cta-btn"
              target="_blank"
              rel="noopener noreferrer"
            >
              <ExternalLink size={14} aria-hidden="true" />
              {t('home_projects_view_btn')}
            </a>
          ) : (
            <span className="rihen-btn projects-structure__cta-btn projects-structure__cta-btn--disabled" aria-disabled="true">
              <ExternalLink size={14} aria-hidden="true" />
              {t('home_projects_view_btn')}
            </span>
          )}
          <button
            type="button"
            className="rihen-btn projects-structure__nav-btn"
            onClick={onNext}
            aria-label={t('home_projects_next_aria')}
          >
            {t('home_projects_next_aria')}
            <ChevronRight size={12} aria-hidden="true" />
          </button>
        </div>
      </div>
    </motion.article>
  );
};

const ProjectsAlbumCarousel = ({ projects, title, titleLine2, subtitle }) => {
  const { t } = useLocale();
  const reduceMotion = useReducedMotion();
  const [activeCategory, setActiveCategory] = useState(FILTER_ALL);
  const [activeIndex, setActiveIndex] = useState(0);
  const [pageIndex, setPageIndex] = useState(0);
  const [direction, setDirection] = useState(0);
  const [shotIndex, setShotIndex] = useState(0);
  const [hoveredGridIndex, setHoveredGridIndex] = useState(null);
  const [gridCols, setGridCols] = useState(GRID_COLS_DESKTOP);
  const gridRef = useRef(null);
  const panelRef = useRef(null);

  const handleTileHoverEnd = useCallback((event) => {
    if (!gridRef.current?.contains(event.relatedTarget)) {
      setHoveredGridIndex(null);
    }
  }, []);

  useEffect(() => {
    const media = window.matchMedia(`(max-width: ${GRID_BREAKPOINT}px)`);
    const syncCols = () => setGridCols(media.matches ? GRID_COLS_MOBILE : GRID_COLS_DESKTOP);
    syncCols();
    media.addEventListener('change', syncCols);
    return () => media.removeEventListener('change', syncCols);
  }, []);

  const categories = useMemo(
    () => [FILTER_ALL, ...Array.from(new Set(projects.map((p) => p.category).filter(Boolean)))],
    [projects],
  );
  const visibleProjects = useMemo(() => {
    if (activeCategory === FILTER_ALL) return projects;
    return projects.filter((project) => project.category === activeCategory);
  }, [projects, activeCategory]);

  const total = visibleProjects.length;
  const totalPages = Math.max(1, Math.ceil(total / PROJECTS_PER_PAGE));
  const pageStart = pageIndex * PROJECTS_PER_PAGE;
  const paginatedProjects = visibleProjects.slice(pageStart, pageStart + PROJECTS_PER_PAGE);
  const activeProject = visibleProjects[activeIndex] ?? visibleProjects[0];
  const shots = activeProject?.images?.length ? activeProject.images : activeProject ? [activeProject.image] : [];
  const hasMultipleShots = shots.length > 1;

  useEffect(() => {
    setActiveIndex(0);
    setPageIndex(0);
    setDirection(0);
    setShotIndex(0);
  }, [activeCategory]);

  useEffect(() => {
    setPageIndex(Math.min(pageIndex, totalPages - 1));
  }, [pageIndex, totalPages]);

  useEffect(() => {
    setShotIndex(0);
  }, [activeProject?.id]);

  const goTo = useCallback(
    (index) => {
      if (!total) return;
      const next = ((index % total) + total) % total;
      setActiveIndex((prev) => {
        if (next === prev) return prev;
        const forward = (next - prev + total) % total;
        const backward = (prev - next + total) % total;
        setDirection(forward <= backward ? 1 : -1);
        setPageIndex(Math.floor(next / PROJECTS_PER_PAGE));
        return next;
      });
    },
    [total],
  );

  const goNext = useCallback(() => {
    setDirection(1);
    setActiveIndex((prev) => (prev + 1) % total);
  }, [total]);

  const goPrev = useCallback(() => {
    setDirection(-1);
    setActiveIndex((prev) => (prev - 1 + total) % total);
  }, [total]);

  useEffect(() => {
    const onKeyDown = (event) => {
      if (event.key === 'ArrowRight') {
        event.preventDefault();
        goNext();
      }
      if (event.key === 'ArrowLeft') {
        event.preventDefault();
        goPrev();
      }
    };

    window.addEventListener('keydown', onKeyDown);
    return () => window.removeEventListener('keydown', onKeyDown);
  }, [goNext, goPrev]);

  const counter = `${String(activeIndex + 1).padStart(2, '0')} / ${String(total).padStart(2, '0')}`;
  const nextShot = () => setShotIndex((prev) => (prev + 1) % shots.length);
  const prevShot = () => setShotIndex((prev) => (prev - 1 + shots.length) % shots.length);
  const canPrevPage = pageIndex > 0;
  const canNextPage = pageIndex < totalPages - 1;

  const goToPage = (nextPage) => {
    const clamped = Math.max(0, Math.min(nextPage, totalPages - 1));
    setPageIndex(clamped);
    const nextIndex = clamped * PROJECTS_PER_PAGE;
    goTo(nextIndex);
  };

  if (!activeProject) return null;

  const slideVariants = {
    enter: (slideDirection) => ({
      opacity: 0,
      x: slideDirection > 0 ? 24 : -24,
    }),
    center: {
      opacity: 1,
      x: 0,
    },
    exit: (slideDirection) => ({
      opacity: 0,
      x: slideDirection > 0 ? -24 : 24,
    }),
  };

  return (
    <section className="projects-album projects-album--structure" aria-label={t('home_projects_album_label')}>
      <div ref={panelRef} className="projects-structure__panel">
        <div className="projects-structure__left">
          <div className="projects-structure__left-head">
            <SectionHeader
              title={title ?? t('home_section_projects_title_line1')}
              titleLine2={titleLine2 ?? t('home_section_projects_title_line2')}
              subtitle={subtitle ?? t('home_section_projects_subtitle')}
              titleClassName="hero-manifest__title hero-manifest__title--on-surface"
              centered={false}
              className="projects-structure__head"
            />
          </div>
          <div
            className="projects-structure__filters"
            role="tablist"
            aria-label="Filtrage des projets"
            data-reveal
          >
            {categories.map((category) => (
              <button
                key={category}
                type="button"
                role="tab"
                aria-selected={activeCategory === category}
                className={`projects-structure__filter${activeCategory === category ? ' is-active' : ''}`}
                onClick={() => setActiveCategory(category)}
              >
                {category === FILTER_ALL ? t('certifications_filter_all') : category}
              </button>
            ))}
          </div>
          <div
            ref={gridRef}
            className="projects-structure__grid"
            role="tablist"
            aria-label={t('home_projects_album_label')}
            data-reveal="scale"
            data-hovering={hoveredGridIndex !== null ? 'true' : undefined}
            onMouseLeave={() => setHoveredGridIndex(null)}
          >
            {paginatedProjects.map((project, index) => (
              <ProjectTile
                key={project.id}
                project={project}
                index={pageStart + index}
                gridIndex={index}
                cols={gridCols}
                isActive={pageStart + index === activeIndex}
                hoveredGridIndex={hoveredGridIndex}
                reduceMotion={reduceMotion}
                onSelect={(selectedIndex) => goTo(selectedIndex)}
                onHover={setHoveredGridIndex}
                onHoverEnd={handleTileHoverEnd}
              />
            ))}
          </div>
          <div className="projects-structure__pager">
            <button
              type="button"
              className="projects-structure__pager-btn"
              onClick={() => goToPage(pageIndex - 1)}
              aria-label={t('home_projects_prev_aria')}
              disabled={!canPrevPage}
            >
              <ChevronLeft size={16} />
            </button>
            <div className="projects-structure__pager-pages" role="tablist" aria-label="Pagination projets">
              {Array.from({ length: totalPages }, (_, page) => (
                <button
                  key={`page-${page + 1}`}
                  type="button"
                  role="tab"
                  aria-selected={page === pageIndex}
                  className={`projects-structure__pager-page${page === pageIndex ? ' is-active' : ''}`}
                  onClick={() => goToPage(page)}
                >
                  {page + 1}
                </button>
              ))}
            </div>
            <button
              type="button"
              className="projects-structure__pager-btn"
              onClick={() => goToPage(pageIndex + 1)}
              aria-label={t('home_projects_next_aria')}
              disabled={!canNextPage}
            >
              <ChevronRight size={16} />
            </button>
          </div>
        </div>

        <div
          className={`projects-structure__divider${reduceMotion ? ' projects-structure__divider--static' : ''}`}
          aria-hidden="true"
        >
          <span className="projects-structure__divider-track" />
          <span className="projects-structure__divider-beam" />
          <span className="projects-structure__divider-beam projects-structure__divider-beam--echo" />
        </div>

        <div className="projects-structure__right" aria-live="polite" data-reveal="right">
          <AnimatePresence mode="wait" custom={direction} initial={false}>
            <ProjectPreviewPanel
              key={activeProject.id}
              project={activeProject}
              shots={shots}
              shotIndex={shotIndex}
              hasMultipleShots={hasMultipleShots}
              direction={direction}
              reduceMotion={reduceMotion}
              slideVariants={slideVariants}
              parallaxContainerRef={panelRef}
              onPrevShot={prevShot}
              onNextShot={nextShot}
              onPrev={goPrev}
              onNext={goNext}
              t={t}
            />
          </AnimatePresence>
        </div>
      </div>
      <p className="sr-only" aria-live="polite">
        {counter} — {activeProject.title}
      </p>
    </section>
  );
};

export default ProjectsAlbumCarousel;
